/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import EntityTypeBadge from './EntityTypeBadge';
import LifecycleStateBadge from './LifecycleStateBadge';
import SealRiskBadge from './SealRiskBadge';

const COLS = ['Name', 'Type', 'State', 'Risk', 'Created', ''];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function getEntityName(e) {
  // V2 P4 (commit b59d734) settled on `display_name`. Older candidates
  // (`name`, `full_name`, `fullName`) kept as defensive fallbacks for any
  // mid-rollout drift, but `display_name` is the canonical field.
  return e?.display_name
      || e?.displayName
      || e?.name
      || e?.full_name
      || e?.fullName
      || e?.id
      || 'Unnamed';
}

export default function PersonaAgentsList({
  entities,
  loading,
  error,
  v2Status,
  v2Endpoint,
  onRowClick,
  onRetry,
}) {
  // ─── Loading ───
  if (loading) {
    return (
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '60px 24px',
        textAlign: 'center',
        color: '#64748b',
      }}>
        <div style={{ fontSize: '28px', marginBottom: '12px' }}>⏳</div>
        Loading persona agents...
      </div>
    );
  }

  // ─── Permission error ───
  if (error?.kind === 'permission') {
    return (
      <div style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        padding: '24px',
        color: '#991b1b',
      }}>
        <strong>🔒 Access denied.</strong>{' '}
        Your admin account is missing the <code>persona.view</code> permission.
        Contact a super-admin to grant access.
      </div>
    );
  }

  // ─── Generic error ───
  if (error?.kind === 'server' || error?.kind === 'auth') {
    return (
      <div style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        padding: '24px',
      }}>
        <div style={{ color: '#991b1b', marginBottom: '12px' }}>
          <strong>⚠️ Could not load persona agents.</strong>
          <div style={{ fontSize: '13px', marginTop: '6px' }}>
            {error.message || 'Unknown error'}
          </div>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#991b1b',
              border: '1px solid #991b1b',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // ─── Coming soon (V2 returned 501) ───
  if (v2Status === 'coming_soon') {
    return (
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '40px 24px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block',
          fontSize: '11px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '12px',
          background: '#fef3c7',
          color: '#92400e',
          marginBottom: '16px',
        }}>
          Coming soon
        </div>
        <h3 style={{
          margin: '0 0 8px',
          fontSize: '16px',
          color: '#1e293b',
        }}>
          Persona Agent management is being implemented.
        </h3>
        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 12px' }}>
          Backend endpoints will be live shortly. This page will populate
          automatically when the upstream service responds with data.
        </p>
        {v2Endpoint && (
          <p style={{
            color: '#94a3b8',
            fontSize: '12px',
            fontFamily: 'monospace',
            margin: 0,
          }}>
            Pending endpoint: {v2Endpoint}
          </p>
        )}
      </div>
    );
  }

  // ─── Empty (V2 live but no entities) ───
  if (!entities || entities.length === 0) {
    return (
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '60px 24px',
        textAlign: 'center',
        color: '#64748b',
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤖</div>
        <h3 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '16px' }}>
          No persona agents yet
        </h3>
        <p style={{ fontSize: '14px', margin: 0 }}>
          Create one with the <strong>+ New</strong> button when it&apos;s enabled.
        </p>
      </div>
    );
  }

  // ─── Data table ───
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
        }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {COLS.map((c) => (
                <th key={c} style={{
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entities.map((entity) => {
              const id = entity?.id || entity?._id;
              return (
                <tr
                  key={id || getEntityName(entity)}
                  onClick={() => onRowClick && onRowClick(id, entity)}
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 500 }}>
                    {getEntityName(entity)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <EntityTypeBadge type={entity?.entity_type || entity?.type} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <LifecycleStateBadge state={entity?.lifecycle_state || entity?.state} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <SealRiskBadge
                      riskScore={entity?.seal_risk_score ?? entity?.risk_score}
                    />
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px' }}>
                    {formatDate(entity?.created_at || entity?.createdAt)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>View →</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
